import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const Schema = z.object({
  name: z.string().min(1),
  scheduleCron: z.string().min(1),
  signals: z.array(z.enum(['no_sales_in_window', 'stale_inventory'])).min(1),
  channels: z.array(z.enum(['email', 'in_app'])).min(1),
  noSalesWindowDays: z.number().int().min(1).max(365),
  staleInventoryDays: z.number().int().min(1).max(365),
});
export type AlertRuleValues = z.infer<typeof Schema>;

const PRESETS = [
  { label: 'Every Monday 9am', value: '0 9 * * MON' },
  { label: 'Daily at 8am', value: '0 8 * * *' },
  { label: 'Every hour', value: '0 * * * *' },
];

export function AlertRuleForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: Partial<AlertRuleValues>;
  onSubmit: (v: AlertRuleValues) => void | Promise<void>;
  onCancel?: () => void;
}): JSX.Element {
  const { register, handleSubmit, watch, setValue, formState } = useForm<AlertRuleValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      name: 'Weekly underperformer digest',
      scheduleCron: '0 9 * * MON',
      signals: ['no_sales_in_window', 'stale_inventory'],
      channels: ['email', 'in_app'],
      noSalesWindowDays: 30,
      staleInventoryDays: 90,
      ...initial,
    },
  });
  const cronVal = watch('scheduleCron');

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <label style={{ fontSize: 12 }}>
        Name
        <input
          {...register('name')}
          style={{
            width: '100%',
            padding: '7px 10px',
            border: '1px solid var(--c-border-strong)',
            borderRadius: 8,
          }}
        />
      </label>
      <div>
        <label style={{ fontSize: 12 }}>Schedule</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
          {PRESETS.map((p) => (
            <button
              type="button"
              key={p.value}
              onClick={() => setValue('scheduleCron', p.value)}
              style={{
                fontSize: 11,
                padding: '4px 10px',
                border: '1px solid var(--c-border-strong)',
                borderRadius: 99,
                background: cronVal === p.value ? 'var(--c-primary-soft)' : '#fff',
                cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <input
          {...register('scheduleCron')}
          style={{
            width: '100%',
            padding: '7px 10px',
            border: '1px solid var(--c-border-strong)',
            borderRadius: 8,
            fontFamily: 'monospace',
          }}
        />
      </div>
      <fieldset style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: 10 }}>
        <legend style={{ fontSize: 12 }}>Signals</legend>
        <label>
          <input type="checkbox" value="no_sales_in_window" {...register('signals')} /> No sales in
          window
        </label>
        <br />
        <label>
          <input type="checkbox" value="stale_inventory" {...register('signals')} /> Stale inventory
        </label>
      </fieldset>
      <fieldset style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: 10 }}>
        <legend style={{ fontSize: 12 }}>Channels</legend>
        <label>
          <input type="checkbox" value="email" {...register('channels')} /> Email
        </label>
        <br />
        <label>
          <input type="checkbox" value="in_app" {...register('channels')} /> In-app notification
        </label>
      </fieldset>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, flex: 1 }}>
          No-sales window (days)
          <input
            type="number"
            {...register('noSalesWindowDays', { valueAsNumber: true })}
            style={{
              width: '100%',
              padding: '7px 10px',
              border: '1px solid var(--c-border-strong)',
              borderRadius: 8,
            }}
          />
        </label>
        <label style={{ fontSize: 12, flex: 1 }}>
          Stale inventory (days)
          <input
            type="number"
            {...register('staleInventoryDays', { valueAsNumber: true })}
            style={{
              width: '100%',
              padding: '7px 10px',
              border: '1px solid var(--c-border-strong)',
              borderRadius: 8,
            }}
          />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          disabled={formState.isSubmitting}
          style={{
            background: 'var(--c-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 14px',
            cursor: 'pointer',
          }}
        >
          Save rule
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              background: 'transparent',
              border: '1px solid var(--c-border-strong)',
              borderRadius: 8,
              padding: '8px 14px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
